import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
  Skeleton,
  Tabs,
  Tab,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import axios from "axios";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";

const Coupons = () => {
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);

  // Search
  const [query, setQuery] = useState("");

  // Tabs like screenshot
  const [tab, setTab] = useState("ACTIVE"); // ACTIVE | INACTIVE

  const getCoupons = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(
        `${API.BASE_URL}coupons?isCouponAllocated=false&page=1&limit=2000`,
        { headers }
      );

      const list = res?.data?.data?.coupons;
      setCoupons(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to fetch offers", {
        position: "top-right",
        autoClose: 3000,
        theme: "colored",
      });
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isActive = (c) => String(c?.status || "").toUpperCase() === "ACTIVE";

  const buildLabel = (arr, fallback) => {
    if (!Array.isArray(arr) || arr.length === 0) return fallback;
    const clean = arr.filter(Boolean);
    if (clean.length === 0) return fallback;
    if (clean.length <= 2) return clean.join(", ");
    return `${clean.slice(0, 2).join(", ")} +${clean.length - 2} more`;
  };

  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return coupons;
    return coupons.filter((c) => {
      const title = (c?.title || "").toLowerCase();
      const code = (c?.couponCode || "").toLowerCase();
      const desc = (c?.description || "").toLowerCase();
      return title.includes(q) || code.includes(q) || desc.includes(q);
    });
  }, [coupons, query]);

  const activeList = useMemo(() => searched.filter((c) => isActive(c)), [searched]);
  const inactiveList = useMemo(() => searched.filter((c) => !isActive(c)), [searched]);

  const list = tab === "ACTIVE" ? activeList : inactiveList;

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            Coupons
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? "Loading..." : `${list.length} shown`}
          </Typography>
        </Box>

        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search coupon / title"
          size="small"
          sx={{ width: { xs: "100%", sm: 360 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* Tabs like screenshot */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: "background.paper",
          mb: 2,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 42,
            "& .MuiTab-root": {
              minHeight: 42,
              fontWeight: 900,
              textTransform: "none",
            },
          }}
        >
          <Tab value="ACTIVE" label={`ACTIVE (${activeList.length})`} />
          <Tab value="INACTIVE" label={`INACTIVE (${inactiveList.length})`} />
        </Tabs>
      </Box>

      {/* Body */}
      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="text" width="55%" />
                  <Skeleton variant="rounded" height={70} sx={{ mt: 1 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : list.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            No coupons found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try changing search or switching tab.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {list.map((c) => {
            const active = isActive(c);

            const categoryNames = Array.isArray(c?.applicableCategories)
              ? c.applicableCategories.map((x) => x?.name).filter(Boolean)
              : [];

            const productNames = Array.isArray(c?.applicableProducts)
              ? c.applicableProducts.map((x) => x?.name).filter(Boolean)
              : [];

            const excludedOutletNames = Array.isArray(c?.excludedOutlets)
              ? c.excludedOutlets.map((x) => x?.name).filter(Boolean)
              : [];

            const categoriesLabel = buildLabel(categoryNames, "Applicable For All Categories");
            const productsLabel = buildLabel(productNames, "Applicable For All Products");
            const excludedLabel = buildLabel(excludedOutletNames, "Applicable In All Outlets");

            const ribbonText =
              c?.couponTypeLabel ||
              (c?.isMrpDiscount
                ? "MRP Discount"
                : c?.isCashbackCoupon
                ? "Cashback Coupon"
                : c?.isFreebieCoupon
                ? "Freebie Coupon"
                : c?.isBogoCoupon
                ? "BOGO Coupon"
                : "Coupon");

            return (
              <Grid item xs={12} sm={6} md={3} key={c?._id || c?.couponCode}>
                <Card
                  variant="outlined"
                  sx={{
                    position: "relative",
                    borderRadius: 2,
                    overflow: "hidden",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    transition: "0.15s ease",
                    "&:hover": { boxShadow: 3, transform: "translateY(-1px)" },
                  }}
                >
                  {/* Left Ribbon */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 46,
                      bgcolor: active ? "#2E7D32" : "action.disabledBackground",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        color: active ? "#fff" : "text.secondary",
                        fontWeight: 900,
                        fontSize: 12,
                        letterSpacing: 0.3,
                        writingMode: "vertical-rl",
                        transform: "rotate(180deg)",
                        userSelect: "none",
                      }}
                    >
                      {ribbonText}
                    </Typography>
                  </Box>

                  <CardContent sx={{ pl: 7, pr: 1.5, py: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 900,
                            fontSize: 12,
                            color: "#FF6A00",
                            letterSpacing: 0.5,
                          }}
                        >
                          {String(c?.couponCode || "—").toUpperCase()}
                        </Typography>

                        <Typography
                          sx={{
                            fontWeight: 900,
                            fontSize: 15,
                            lineHeight: 1.2,
                            mt: 0.2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={c?.title || "Coupon"}
                        >
                          {c?.title || "Coupon"}
                        </Typography>
                      </Box>

                      <IconButton size="small" sx={{ mt: -0.4 }}>
                        <MoreVertRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    <InfoLine label="Applicable Categories" value={categoriesLabel} />
                    <InfoLine label="Applicable Products" value={productsLabel} />
                    <InfoLine label="Excluding Outlets" value={excludedLabel} />
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

const InfoLine = ({ label, value }) => {
  return (
    <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.7, lineHeight: 1.25 }}>
      <Box component="span" sx={{ fontWeight: 800 }}>
        {label}
      </Box>
      :{" "}
      <Box component="span" sx={{ color: "text.primary" }}>
        {value}
      </Box>
    </Typography>
  );
};

export default Coupons;